import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { memo, ReactElement, SyntheticEvent, useCallback, useMemo } from 'react';

import {
  SECTION_ACCORDION_SX,
  SECTION_NUMBER_SX,
  SECTION_SUMMARY_SX,
  SECTION_TITLE_SX,
  SectionSummaryMeta,
} from 'components/common/checklistTree';
import { MemoSectionNotApplicableToggle } from 'components/inspection/SectionNotApplicableToggle';
import { SectionTreeNode } from 'shared/domain/checklistTree';

const sectionSummarySx = {
  ...SECTION_SUMMARY_SX,
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    overflow: 'visible',
    minWidth: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    ml: 2,
    flexShrink: 0,
  },
};

const sectionSummaryEndSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  pl: 1,
  ml: 'auto',
  flexShrink: 0,
  minWidth: 0,
} satisfies SxProps<Theme>;

const expandIcon = <ExpandMoreIcon />;

function stopPropagation(event: SyntheticEvent): void {
  event.stopPropagation();
}

interface InspectionSectionHeaderRowProps {
  node: SectionTreeNode;
  expanded: boolean;
  /** Section rows are shown below (expanded or animating out). */
  frameOpen: boolean;
  onExpandedChange: (id: string, expanded: boolean) => void;
  answeredItems: number;
  totalItems: number;
  canSkip: boolean;
  isNotApplicable: boolean;
  onSkippedChange?: (sectionId: string, skipped: boolean) => void;
}

function InspectionSectionHeaderRow({
  node,
  expanded,
  frameOpen,
  onExpandedChange,
  answeredItems,
  totalItems,
  canSkip,
  isNotApplicable,
  onSkippedChange,
}: InspectionSectionHeaderRowProps): ReactElement {
  const handleChange = useCallback(
    (_: SyntheticEvent, isExpanded: boolean) =>
      onExpandedChange(node.id, isExpanded),
    [node.id, onExpandedChange]
  );

  // An open section continues below in the rows' frame, so the header drops
  // its bottom edge and bottom corners (MUI rounds them via :last-of-type,
  // which outranks a plain `borderRadius`); spacing comes from row padding.
  const accordionSx = useMemo(
    () => ({
      ...SECTION_ACCORDION_SX,
      mb: 0,
      '&.Mui-expanded': { m: 0 },
      ...(frameOpen
        ? {
            borderBottom: 0,
            '&, &:last-of-type': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          }
        : {}),
    }),
    [frameOpen]
  );

  return (
    <Accordion expanded={expanded} onChange={handleChange} sx={accordionSx}>
      <AccordionSummary expandIcon={expandIcon} sx={sectionSummarySx}>
        <Typography
          variant='body1'
          fontWeight={expanded ? 700 : 400}
          sx={SECTION_NUMBER_SX}
        >
          {node.displayNumber}
        </Typography>
        <Typography
          variant='body1'
          fontWeight={expanded ? 700 : 400}
          sx={SECTION_TITLE_SX}
        >
          {node.data.title}
        </Typography>
        <Box onClick={stopPropagation} onKeyDown={stopPropagation}>
          <Stack direction='row' alignItems='center' sx={sectionSummaryEndSx}>
            <SectionSummaryMeta
              answeredItems={answeredItems}
              totalItems={totalItems}
              isNotApplicable={isNotApplicable}
            />
            <MemoSectionNotApplicableToggle
              sectionId={node.id}
              canSkip={canSkip}
              isNotApplicable={isNotApplicable}
              onSkippedChange={onSkippedChange}
            />
          </Stack>
        </Box>
      </AccordionSummary>
    </Accordion>
  );
}

export const MemoInspectionSectionHeaderRow = memo(InspectionSectionHeaderRow);
